/**
 * Wire schemas: the runtime contract between PHP's JSON and the types a
 * binding declares.
 *
 * PHP's PDO hands most scalars back as strings and flags as `'0'`/`'1'`/`0`/`1`
 * depending on how the row was fetched. The web tolerates that; the native
 * apps decode these payloads into strict Swift/Kotlin types and fail the whole
 * response. A binding that passes a schema to its `send` seam guarantees every
 * host gets exactly the declared runtime types.
 *
 * Builders accept every spelling the API emits and output one type. Objects
 * are LOOSE: undeclared keys pass through untouched, so a schema only has to
 * name the fields it converts or vouches for.
 */

import * as z from 'zod/mini';

export type WireSchema<T = unknown> = z.ZodMiniType<T, unknown>;

const INT_STRING = /^\s*-?\d+\s*$/;
const NUMBER_STRING = /^\s*-?(\d+\.?\d*|\.\d+)([eE][-+]?\d+)?\s*$/;

/**
 * An integer as a number or a numeric string → `number`. Also the right
 * builder for ids a package's types declare as `number`; `wireId` is only for
 * ids whose type (and consumers) accept a string.
 */
export const wireInt: WireSchema<number> = z.pipe(
  z.union([z.int(), z.string().check(z.regex(INT_STRING))]),
  z.transform((v: number | string) => Number(v))
);

/** A finite number or numeric string → `number`. Never use for money; see `wireDecimal`. */
export const wireNumber: WireSchema<number> = z.pipe(
  z.union([
    z.number(),
    z.string().check(
      z.regex(NUMBER_STRING),
      z.refine((v: string) => Number.isFinite(Number(v)))
    ),
  ]),
  z.transform((v: number | string) => Number(v))
);

/** A server flag in any spelling (`1`, `'1'`, `true`, `0`, `'0'`, `false`, `''`) → `boolean`. */
export const wireBool: WireSchema<boolean> = z.pipe(
  z.union([z.boolean(), z.literal(0), z.literal(1), z.literal('0'), z.literal('1'), z.literal('')]),
  z.transform((v: boolean | 0 | 1 | '0' | '1' | '') => v === true || v === 1 || v === '1')
);

/**
 * A MySQL `DECIMAL` → its lossless string. A string is kept verbatim
 * (`"20.00"` stays `"20.00"`); a number is stringified, never rounded.
 */
export const wireDecimal: WireSchema<string> = z.pipe(
  z.union([z.number(), z.string().check(z.regex(NUMBER_STRING))]),
  z.transform((v: number | string) => (typeof v === 'number' ? String(v) : v))
);

/** An id the server sends as a number or a string → `string`. */
export const wireId: WireSchema<string> = z.pipe(
  z.union([z.number(), z.string()]),
  z.transform((v: number | string) => String(v))
);

/**
 * The `{response: T[], count}` list envelope. `count` becomes a number; sibling
 * members (e.g. a sellables `stats`) pass through unchanged.
 */
export function listEnvelope<Row extends z.ZodMiniType>(row: Row) {
  return z.looseObject({ response: z.array(row), count: wireInt });
}

/** A request body as PHP reads it: booleans become `1`/`0`, recursively. */
export function toWire<T>(value: T): unknown {
  if (value === true) return 1;
  if (value === false) return 0;
  if (Array.isArray(value)) return value.map(toWire);
  if (value !== null && typeof value === 'object' && Object.getPrototypeOf(value) === Object.prototype) {
    const out: Record<string, unknown> = {};
    for (const [key, v] of Object.entries(value as Record<string, unknown>)) out[key] = toWire(v);
    return out;
  }
  return value;
}

// ── Mismatch policy ─────────────────────────────────────────────────────

/**
 * `'strict'` throws on a mismatch (tests, dev). `'report'` resolves a
 * best-effort body — every field that parses is converted, a missing key stays
 * missing, and only the failing subtree (a leaf, or a null/wrong-shape object
 * or array) keeps its raw value — calls the reporter, and lets the caller carry
 * on. A production host must not break because the server added a spelling
 * the schema missed.
 */
export type SchemaMode = 'strict' | 'report';

export interface SchemaMismatch {
  /** `'GET /api/service/tickets'` — method plus path, query stripped. */
  endpoint: string;
  /** One entry per failing field, e.g. `'response.3.timestamp'`. */
  paths: string[];
  message: string;
}

export type SchemaMismatchReporter = (mismatch: SchemaMismatch) => void;

// Build-time constant: test-shared-ts.mjs defines it as 'strict'; every other
// bundle leaves it undefined and gets the production-safe 'report'.
declare const __DEALR_API_SCHEMA_MODE__: SchemaMode | undefined;

let mode: SchemaMode =
  typeof __DEALR_API_SCHEMA_MODE__ !== 'undefined' && __DEALR_API_SCHEMA_MODE__ === 'strict' ? 'strict' : 'report';
let reporter: SchemaMismatchReporter = () => undefined;

export function setSchemaMode(next: SchemaMode): void {
  mode = next;
}

export function getSchemaMode(): SchemaMode {
  return mode;
}

/** Pass `null` to restore the no-op default. */
export function setSchemaMismatchReporter(next: SchemaMismatchReporter | null): void {
  reporter = next ?? (() => undefined);
}

export class SchemaMismatchError extends Error {
  readonly mismatch: SchemaMismatch;
  constructor(mismatch: SchemaMismatch) {
    super(mismatch.message);
    this.name = 'SchemaMismatchError';
    this.mismatch = mismatch;
  }
}

/** Parses `data` with `schema` under the current mode. See `SchemaMode`. */
export function parseWire<T>(schema: WireSchema<T>, data: unknown, endpoint: string): T {
  const result = schema.safeParse(data);
  if (result.success) return result.data;
  const paths = result.error.issues.map((issue) => issue.path.map(String).join('.') || '(root)');
  const mismatch: SchemaMismatch = {
    endpoint,
    paths,
    message: `Response from ${endpoint} does not match its schema at ${paths.join(', ')}`,
  };
  if (mode === 'strict') throw new SchemaMismatchError(mismatch);
  try {
    reporter(mismatch);
  } catch {
    // A failing reporter must not turn a tolerated mismatch into a failure.
  }
  try {
    return bestEffort(schema, data) as T;
  } catch {
    return data as T;
  }
}

interface SchemaDef {
  type: string;
  shape?: Record<string, WireSchema>;
  element?: WireSchema;
  innerType?: WireSchema;
  valueType?: WireSchema;
}

const isPlainObject = (v: unknown): v is Record<string, unknown> =>
  v !== null && typeof v === 'object' && !Array.isArray(v);

// Walks objects, arrays and records field by field so one bad field costs only
// that field. Any other schema (leaf, union, pipe) that fails keeps its raw value.
function bestEffort(schema: WireSchema, value: unknown): unknown {
  const whole = schema.safeParse(value);
  if (whole.success) return whole.data;
  const def = (schema as unknown as { _zod: { def: SchemaDef } })._zod.def;
  switch (def.type) {
    case 'object': {
      if (!isPlainObject(value) || !def.shape) return value;
      const out: Record<string, unknown> = { ...value };
      for (const [key, field] of Object.entries(def.shape)) {
        if (key in value) out[key] = bestEffort(field, value[key]);
      }
      return out;
    }
    case 'array':
      return Array.isArray(value) && def.element ? value.map((item) => bestEffort(def.element!, item)) : value;
    case 'record': {
      if (!isPlainObject(value) || !def.valueType) return value;
      const out: Record<string, unknown> = {};
      for (const [key, v] of Object.entries(value)) out[key] = bestEffort(def.valueType, v);
      return out;
    }
    case 'optional':
    case 'nullable':
    case 'default':
    case 'readonly':
      return value === undefined || value === null || !def.innerType ? value : bestEffort(def.innerType, value);
    default:
      return value;
  }
}

/** The PII-free, low-cardinality form of a mismatch that error trackers group on. */
export interface MismatchReport {
  /** Constant, so every mismatch shares one message and grouping uses `fingerprint`. */
  message: 'API schema mismatch';
  method: string;
  /**
   * The endpoint's route template when the binding supplied one, else its
   * path with every segment that could carry a value replaced by `:param`.
   */
  path: string;
  /** Failing field paths with array indices collapsed to `*` and record keys to `:key`, deduped and capped. */
  fields: string[];
  /** How many distinct collapsed field paths failed, before the cap. */
  fieldCount: number;
  fingerprint: string[];
}

const MAX_REPORTED_FIELDS = 20;
// A static route word: letters with optional `_`/`-`, short. Anything else —
// digits (ids, VINs, stock numbers, phones), `@`, `.`, `%`-escapes, long
// free text — may be caller data and is redacted.
const STATIC_SEGMENT = /^[A-Za-z][A-Za-z_-]{0,39}$/;
const TEMPLATE_PARAM = /^:[A-Za-z_]\w*$/;
const ID_SEGMENT = /^(\d+|[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12})$/i;

// A field name in a failing issue path. Anything else is a record key (a
// UUID, VIN or id the server chose) and is collapsed.
const FIELD_SEGMENT = /^[A-Za-z_][A-Za-z_]{0,39}\d{0,2}$/;

function redactField(segment: string): string {
  if (/^\d+$/.test(segment)) return '*';
  return FIELD_SEGMENT.test(segment) ? segment : ':key';
}

function redactSegment(segment: string): string {
  if (segment === '' || STATIC_SEGMENT.test(segment) || TEMPLATE_PARAM.test(segment)) return segment;
  return ID_SEGMENT.test(segment) ? ':id' : ':param';
}

/**
 * Normalizes a mismatch for an error tracker: path segments that may carry
 * caller data become `:id` (numeric/UUID) or `:param` (a binding's route template, passed as
 * `HttpRequest.route`, keeps its named params), row indices in field paths
 * become `*`, record keys become `:key`, and no field value is ever included. The output is a Sentry tag
 * and fingerprint, so it must hold no PII and stay low-cardinality.
 */
export function describeMismatch(mismatch: SchemaMismatch): MismatchReport {
  const space = mismatch.endpoint.indexOf(' ');
  const method = space > 0 ? mismatch.endpoint.slice(0, space) : '';
  const rawPath = (space > 0 ? mismatch.endpoint.slice(space + 1) : mismatch.endpoint).split('?')[0] ?? '';
  const path = rawPath
    .split('/')
    .map(redactSegment)
    .join('/');
  const distinct = [
    ...new Set(
      mismatch.paths.map((field) =>
        field
          .split('.')
          .map(redactField)
          .join('.')
      )
    ),
  ];
  return {
    message: 'API schema mismatch',
    method,
    path,
    fields: distinct.slice(0, MAX_REPORTED_FIELDS),
    fieldCount: distinct.length,
    fingerprint: ['api-schema-mismatch', method, path],
  };
}

/**
 * `'GET /api/x'` for a request, query stripped, for mismatch reports. Uses the
 * request's route template (`/api/service/checkIn/vinCheck/:vin`) when the
 * binding declares one, so the concrete value never enters the report.
 */
export function endpointLabel(req: { method: string; path: string; route?: string }): string {
  return `${req.method} ${(req.route ?? req.path).split('?')[0]}`;
}
